import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth, api } from '../context/AuthContext'

const C = {
  bg:          '#F6F6F3',
  card:        '#FFFFFF',
  border:      '#e9e9e4',
  orange:      '#E8440A',
  text:        '#111317',
  muted:       '#9a9a94',
  label:       '#374151',
  inputBg:     '#FFFFFF',
  inputBorder: '#e9e9e4',
  inputFocus:  '#E8440A',
  // keep alias names used in sub-components
  white:       '#111317',
  white55:     '#9a9a94',
  white35:     '#9a9a94',
  navyLine:    '#e9e9e4',
  navyMid:     '#FFFFFF',
  navy:        '#F6F6F3',
}


function InputField({ label, icon, type = 'text', value, onChange, onFocus, onBlur, focused, placeholder, autoComplete, maxLength, disabled }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace" }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        {icon && <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none' }}>{icon}</span>}
        <input
          type={type} value={value} onChange={onChange}
          onFocus={onFocus} onBlur={onBlur}
          placeholder={placeholder} autoComplete={autoComplete}
          maxLength={maxLength} disabled={disabled}
          style={{
            width: '100%', height: 48, borderRadius: 10, boxSizing: 'border-box',
            border: `1.5px solid ${focused ? C.inputFocus : C.inputBorder}`,
            background: disabled ? '#f9f9f7' : C.inputBg,
            color: disabled ? C.muted : C.text,
            padding: icon ? '0 14px 0 44px' : '0 14px',
            fontSize: 14, outline: 'none',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'border-color 0.2s',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      </div>
    </div>
  )
}

// ── OTP digit cells ───────────────────────────────────────────────────────────
function OTPInput({ value, onChange }) {
  const refs = useRef([])

  useEffect(() => { refs.current[0]?.focus() }, [])

  const handleChange = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1)
    if (!digit) return
    const arr = (value + '      ').slice(0, 6).split('')
    arr[i] = digit
    const next = arr.join('').trimEnd()
    onChange(next.slice(0, 6))
    if (i < 5) refs.current[i + 1]?.focus()
  }

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const arr = (value + '      ').slice(0, 6).split('')
      arr[i] = ' '
      const next = arr.join('').trimEnd()
      onChange(next.slice(0, 6))
      if (i > 0) refs.current[i - 1]?.focus()
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus()
    } else if (e.key === 'ArrowRight' && i < 5) {
      refs.current[i + 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    onChange(pasted)
    refs.current[Math.min(pasted.length, 5)]?.focus()
  }

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {Array.from({ length: 6 }, (_, i) => {
        const digit    = value[i] || ''
        const isCurrent = i === value.length
        return (
          <input
            key={i}
            ref={el => refs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={e => handleChange(i, e)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onClick={() => refs.current[i]?.select()}
            style={{
              width: 48, height: 56, borderRadius: 10, textAlign: 'center',
              fontSize: 22, fontWeight: 800, color: C.text,
              fontFamily: "'JetBrains Mono', monospace",
              border: `1.5px solid ${digit ? C.orange : isCurrent ? 'rgba(232,68,10,0.5)' : C.inputBorder}`,
              background: digit ? '#fff3f0' : C.inputBg,
              outline: 'none', caretColor: 'transparent',
              transition: 'all 0.15s',
              boxShadow: isCurrent ? '0 0 0 3px rgba(232,68,10,0.12)' : 'none',
            }}
          />
        )
      })}
    </div>
  )
}

// ── Phone / OTP flow ──────────────────────────────────────────────────────────
function PhoneFlow({ onSuccess }) {
  // `digits` holds only the local part — e.g. "788228847"
  // full phone is always "+250" + digits, no normalisation needed
  const [digits,    setDigits]    = useState('')
  const [otp,       setOtp]       = useState('')
  const [step,      setStep]      = useState('phone')
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')
  const [countdown, setCountdown] = useState(0)
  const [focused,   setFocused]   = useState(false)
  const timerRef = useRef(null)

  const fullPhone = `+250${digits.replace(/\D/g, '')}`

  const startCountdown = (secs = 60) => {
    setCountdown(secs)
    clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(timerRef.current); return 0 } return c - 1 })
    }, 1000)
  }
  useEffect(() => () => clearInterval(timerRef.current), [])

  const sendOTP = async () => {
    const localDigits = digits.replace(/\D/g, '')
    if (localDigits.length < 9) { setError('Enter your 9-digit phone number.'); return }
    setLoading(true); setError('')
    try {
      await api.post('/user/otp/send', { phone: fullPhone })
      setStep('otp')
      setOtp('')
      startCountdown(60)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code. Try again.')
    } finally { setLoading(false) }
  }

  const verifyOTP = async () => {
    if (otp.length < 6) { setError('Enter all 6 digits.'); return }
    setLoading(true); setError('')
    try {
      const res = await api.post('/user/otp/verify', { phone: fullPhone, code: otp })
      const token = res.data.token || res.data.accessToken
      if (!token) { setError('Verification failed — no token received.'); return }
      onSuccess(token)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code.')
    } finally { setLoading(false) }
  }

  const btnStyle = (disabled) => ({
    width: '100%', height: 48, borderRadius: 10, border: 'none',
    background: disabled ? 'rgba(232,68,10,0.4)' : C.orange,
    color: '#fff', fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: disabled ? 'none' : '0 4px 16px rgba(232,68,10,0.35)',
    transition: 'all 0.2s',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {step === 'phone' ? (
        <>
          {/* Phone input with locked +250 prefix */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace" }}>
              Phone Number
            </label>
            <div style={{ display: 'flex', height: 48, borderRadius: 10, overflow: 'hidden', border: `1.5px solid ${focused ? C.orange : C.inputBorder}`, transition: 'border-color 0.2s', background: C.inputBg }}>
              {/* Locked prefix */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', background: '#f3f4f6', borderRight: `1px solid ${C.inputBorder}`, fontSize: 14, fontWeight: 700, color: C.muted, fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap', userSelect: 'none' }}>
                🇷🇼 +250
              </div>
              {/* Digits only */}
              <input
                type="tel"
                inputMode="numeric"
                value={digits}
                onChange={e => setDigits(e.target.value.replace(/\D/g, '').slice(0, 9))}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="788 228 847"
                autoComplete="tel-local"
                style={{ flex: 1, height: '100%', background: 'transparent', border: 'none', outline: 'none', padding: '0 14px', fontSize: 15, fontWeight: 600, color: C.text, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
              />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 5, fontFamily: "'JetBrains Mono', monospace" }}>
              Full number: {digits.replace(/\D/g, '').length >= 1 ? fullPhone : '+250…'}
            </div>
          </div>

          {error && <ErrorBox msg={error} />}
          <button onClick={sendOTP} disabled={loading} style={btnStyle(loading)}>
            {loading ? 'Sending…' : 'Send Code →'}
          </button>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>Code sent to</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
              {fullPhone}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Valid for 5 minutes</div>
          </div>

          <OTPInput value={otp} onChange={v => { setOtp(v); setError('') }} />

          {error && <ErrorBox msg={error} />}

          <button onClick={verifyOTP} disabled={loading || otp.length < 6} style={btnStyle(loading || otp.length < 6)}>
            {loading ? 'Verifying…' : 'Verify & Sign In →'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
            <button onClick={() => { setStep('phone'); setOtp(''); setError('') }}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12 }}>
              ← Change number
            </button>
            {countdown > 0 ? (
              <span style={{ color: C.muted }}>Resend in {countdown}s</span>
            ) : (
              <button onClick={sendOTP} disabled={loading}
                style={{ background: 'none', border: 'none', color: C.orange, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 12 }}>
                Resend Code
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── Username + Password flow ──────────────────────────────────────────────────
function PasswordFlow({ onSuccess }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [unFocused, setUnFocused] = useState(false)
  const [pwFocused, setPwFocused] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!username.trim()) { setError('Username is required.'); return }
    if (!password.trim()) { setError('Password is required.'); return }
    setLoading(true); setError('')
    try {
      const res = await api.post('/user/login', { username: username.trim(), password: password.trim() })
      const token = res.data.token || res.data.accessToken
      if (!token) { setError('Login failed — no token received.'); return }
      onSuccess(token)
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Invalid username or password.')
    } finally { setLoading(false) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <InputField
        label="Username or Email" icon="👤" value={username}
        onChange={e => setUsername(e.target.value)}
        onFocus={() => setUnFocused(true)} onBlur={() => setUnFocused(false)}
        focused={unFocused} placeholder="your_username" autoComplete="username"
      />
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono', monospace" }}>
            Password
          </label>
          <Link to="/forgot-password" style={{ fontSize: 11, color: '#E8440A', fontWeight: 600, textDecoration: 'none' }}>
            Forgot password?
          </Link>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showPw ? 'text' : 'password'} value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)}
            placeholder="••••••••" autoComplete="current-password"
            style={{
              width: '100%', height: 48, borderRadius: 10, boxSizing: 'border-box',
              border: `1.5px solid ${pwFocused ? C.inputFocus : C.inputBorder}`,
              background: C.inputBg, color: C.text, padding: '0 44px 0 14px',
              fontSize: 14, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'border-color 0.2s',
            }}
          />
          <span onClick={() => setShowPw(p => !p)}
            style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: 16, color: C.muted, userSelect: 'none' }}>
            {showPw ? '🙈' : '👁'}
          </span>
        </div>
      </div>

      {error && <ErrorBox msg={error} />}

      <button type="submit" disabled={loading}
        style={{ width: '100%', height: 48, borderRadius: 10, border: 'none', background: loading ? 'rgba(232,68,10,0.4)' : C.orange, color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: loading ? 'none' : '0 4px 16px rgba(232,68,10,0.35)', transition: 'all 0.2s' }}>
        {loading ? 'Signing in…' : 'Sign In →'}
      </button>
    </form>
  )
}

function ErrorBox({ msg }) {
  return (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
      ⚠ {msg}
    </div>
  )
}

// ── Main Login ────────────────────────────────────────────────────────────────
export default function Login() {
  const { login } = useAuth()
  const [mode,      setMode]      = useState('phone')
  const [authError, setAuthError] = useState('')

  const handleSuccess = (token) => {
    setAuthError('')
    const ok = login(token)
    if (!ok) setAuthError('This account does not have seller access. Ask your platform admin to assign you the seller role.')
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F6F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 20 }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <div style={{ width: '100%', maxWidth: 420, animation: 'fadeUp 0.3s ease' }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, justifyContent: 'center' }}>
          <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(145deg,#E8440A 0%,#c9380a 100%)', display: 'none', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(232,68,10,0.3)' }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>K</span>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.03em', color: '#111317' }}>Kudos Live</div>
            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Seller Portal</div>
          </div>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e9e9e4', boxShadow: '0 4px 32px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

          {/* Mode tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4' }}>
            {[
              { id: 'phone',    label: '📱 Phone (OTP)'      },
              { id: 'password', label: '🔑 Username & Password' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setMode(tab.id)}
                style={{ flex: 1, padding: '14px 0', border: 'none', background: mode === tab.id ? '#fff3f0' : 'transparent', color: mode === tab.id ? C.orange : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s', borderBottom: `2px solid ${mode === tab.id ? C.orange : 'transparent'}`, marginBottom: -1 }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form */}
          <div style={{ padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {mode === 'phone'
              ? <PhoneFlow    onSuccess={handleSuccess} />
              : <PasswordFlow onSuccess={handleSuccess} />
            }
            {authError && <ErrorBox msg={authError} />}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#9a9a94' }}>
          New seller?{' '}
          <Link to="/register" style={{ color: '#E8440A', fontWeight: 700, textDecoration: 'none' }}>Create your account →</Link>
        </div>
      </div>
    </div>
  )
}
